import { PaymentKeyHash } from '@lucid-evolution/lucid';
import { FundFactory, isEqual, isNullOrBlank, OptionsGet, optionsGetDefault, OptionsGetOne, toJson } from '../../Commons';
import { ProtocolEntity } from '../../Entities/Protocol.Entity';
import { SmartDBApi } from '../index.exports';
import { fetchWrapper } from '../../lib/index.exports';

export class ProtocolApi extends SmartDBApi {
    protected static _Entity = ProtocolEntity;

    // #region api

    public static async addFundFactoryApi<T extends ProtocolEntity>(instance: T, fundFactory: FundFactory): Promise<void> {
        try {
            if (isNullOrBlank(instance._DB_id)) {
                throw `id not defined`;
            }
            //-------------------------
            const params = fundFactory;
            const body = toJson(params);
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/update-ff/${instance._DB_id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - addFundFactoryApi - response OK`);
                const instance_ = this._Entity.fromPlainObject<T>(data);
                Object.assign(instance, instance_);
                console.log(`[${this._Entity.className()}] - addFundFactoryApi - Instance: ${instance.show()}`);
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - addFundFactoryApi - Error: ${error}`);
            throw error;
        }
    }

    public static async deleteFundFactoryApi<T extends ProtocolEntity>(instance: T, fundFactory: FundFactory): Promise<void> {
        try {
            //-------------------------
            if (isNullOrBlank(instance._DB_id)) {
                throw `id not defined`;
            }
            //-------------------------
            const params = fundFactory;
            const body = toJson(params);
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/update-ff/${instance._DB_id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - deleteFundFactoryApi - response OK`);
                const instance_ = this._Entity.fromPlainObject<T>(data);
                Object.assign(instance, instance_);
                console.log(`[${this._Entity.className()}] - deleteFundFactoryApi - Instance: ${instance.show()}`);
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - deleteFundFactoryApi - Error: ${error}`);
            throw error;
        }
    }

    public static async isAdminApi(pkh: PaymentKeyHash): Promise<boolean> {
        try {
            if (isNullOrBlank(pkh)) {
                throw `pkh not defined`;
            }
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/is-admin/${pkh}`);
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - isAdminApi - pkh: ${pkh} - isAdmin: ${data.isAdmin} - response OK`);
                return data.isAdmin;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - isAdminApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getByAdminApi<T extends ProtocolEntity>(pkh: PaymentKeyHash, optionsGet?: OptionsGet): Promise<T[]> {
        try {
            if (isNullOrBlank(pkh)) {
                throw `pkh not defined`;
            }
            //------------------
            let response;
            if (isEqual(optionsGet, optionsGetDefault)) {
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/by-admin/${pkh}`);
            } else {
                const body = toJson(optionsGet);
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/by-admin/${pkh}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body,
                });
            }
            //------------------
            if (response.status === 200) {
                const datas = await response.json();
                console.log(`[${this._Entity.className()}] - getByAdminApi - response OK`);
                const instances = datas.map((data: any) => this._Entity.fromPlainObject<T>(data));
                console.log(`[${this._Entity.className()}] - getByAdminApi - pkh: ${pkh} - len: ${instances.length}`);
                return instances;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getByAdminApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getLastProtocolApi<T extends ProtocolEntity>(optionsGet?: OptionsGetOne): Promise<T | undefined> {
        try {
            const instance = await this.getOneByParamsApi_<T>({}, { ...optionsGet, sort: { pdpProtocolVersion: -1 } });
            return instance;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getByAdminApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getProtocolByVersionApi<T extends ProtocolEntity>(protocolVersion: Number, optionsGet?: OptionsGetOne): Promise<T | undefined> {
        try {
            const instance = await this.getOneByParamsApi_<T>({ pdpProtocolVersion: protocolVersion }, optionsGet);
            return instance;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getByAdminApi - Error: ${error}`);
            throw error;
        }
    }

    public static async updateHistoryApi<T extends ProtocolEntity>(forceStart?: boolean): Promise<string> {
        try {
            //-------------------------
            const body = toJson({ forceStart });
            //-------------------------
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/update-history`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            //-------------------------
            if (response.status === 202) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - updateHistoryApi - jobId: ${data.jobId} - response OK`);
                return data.jobId;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
            //-------------------------
        } catch (error) {
            console.log(`[${this._Entity.className()}] - updateHistoryApi - Error: ${error}`);
            throw error;
        }
    }

    // #endregion api
}
